import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Map from 'ol/Map';

interface PolygonBounds {
  bounds: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  };
  center: { lat: number; lng: number };
  zoomLevel: number;
  polygonCount: number;
}

interface MapState {
  mapInstance: Map | null;
  center: [number, number];
  zoom: number;
  selectedCoordinates: { lat: number; lng: number } | null;
  currentProject: any | null;
  mapType: 'satellite' | 'terrain' | 'street';
  layersVisible: {
    basemap: boolean;
    geological: boolean;
    mineral: boolean;
    probability: boolean;
  };
  polygonBounds: PolygonBounds | null;
  shouldCenterOnPolygons: boolean;
}

const initialState: MapState = {
  mapInstance: null,
  center: [-60, -25], // South America center
  zoom: 4,
  selectedCoordinates: null,
  currentProject: null,
  mapType: 'satellite',
  layersVisible: {
    basemap: true,
    geological: false,
    mineral: false,
    probability: false,
  },
  polygonBounds: null,
  shouldCenterOnPolygons: true
};

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setMapInstance: (state, action: PayloadAction<Map | null>) => {
      state.mapInstance = action.payload;
    },
    setCenter: (state, action: PayloadAction<[number, number]>) => {
      state.center = action.payload;
    },
    setZoom: (state, action: PayloadAction<number>) => {
      state.zoom = action.payload;
    },
    setSelectedCoordinates: (state, action: PayloadAction<{ lat: number; lng: number } | null>) => {
      state.selectedCoordinates = action.payload;
    },
    setCurrentProject: (state, action: PayloadAction<any | null>) => {
      state.currentProject = action.payload;
    },
    setMapType: (state, action: PayloadAction<'satellite' | 'terrain' | 'street'>) => {
      state.mapType = action.payload;
    },
    setLayerVisible: (state, action: PayloadAction<{ layer: keyof MapState['layersVisible']; visible: boolean }>) => {
      state.layersVisible[action.payload.layer] = action.payload.visible;
    },
    toggleLayer: (state, action: PayloadAction<keyof MapState['layersVisible']>) => {
      state.layersVisible[action.payload] = !state.layersVisible[action.payload];
    },
    setPolygonBounds: (state, action: PayloadAction<PolygonBounds | null>) => {
      state.polygonBounds = action.payload;
    },
    setShouldCenterOnPolygons: (state, action: PayloadAction<boolean>) => {
      state.shouldCenterOnPolygons = action.payload;
    },
    clearPolygonBounds: (state) => {
      state.polygonBounds = null;
    }
  }
});

export const {
  setMapInstance,
  setCenter,
  setZoom,
  setSelectedCoordinates,
  setCurrentProject,
  setMapType,
  setLayerVisible,
  toggleLayer,
  setPolygonBounds,
  setShouldCenterOnPolygons,
  clearPolygonBounds
} = mapSlice.actions;

export default mapSlice.reducer;